import React, { useEffect, useRef, useState } from 'react';

const Slider = () => {
    const audioRef = useRef(null);
    const videoRef = useRef(null);
    const sectionRef = useRef(null);
    const audioContextRef = useRef(null);
    const sourceRef = useRef(null);
    const [audioEnabled, setAudioEnabled] = useState(false);
    const [audioInitialized, setAudioInitialized] = useState(false);
    const [audioMuted, setAudioMuted] = useState(true);

    useEffect(() => {
        // Set up video playback
        if (videoRef.current) {
            videoRef.current.play().catch(error => {
                console.log('Video autoplay prevented:', error);
            });
        }

        // Set up audio playback using Web Audio API
        const initializeAudio = async () => {
            try {
                // Create audio context
                audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
                
                // Load audio file
                const response = await fetch('/Audio/lofi-instrumental.mp3');
                const arrayBuffer = await response.arrayBuffer();
                const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
                
                // Create audio source
                sourceRef.current = audioContextRef.current.createBufferSource();
                sourceRef.current.buffer = audioBuffer;
                sourceRef.current.loop = true;
                
                // Create gain node for volume control
                const gainNode = audioContextRef.current.createGain();
                gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime); // 30% volume
                
                // Connect audio graph
                sourceRef.current.connect(gainNode);
                gainNode.connect(audioContextRef.current.destination);
                
                console.log('Web Audio API initialized successfully');
                setAudioInitialized(true);
                
                // Start playing immediately (will be silent until user interaction)
                sourceRef.current.start();
                setAudioEnabled(true);
                console.log('Audio started playing (silent until user interaction)');
                
            } catch (error) {
                console.log('Web Audio API initialization failed:', error);
                // Fallback to HTML5 audio
                if (audioRef.current) {
                    audioRef.current.volume = 0.3;
                    audioRef.current.muted = true;
                    setAudioInitialized(true);
                }
            }
        };
        
        initializeAudio();

        // Set up intersection observer to pause music when scrolling away
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting && audioEnabled) {
                        // Section is visible and audio is enabled, resume music
                        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                            audioContextRef.current.resume();
                        }
                    } else {
                        // Section is not visible, pause music
                        if (audioContextRef.current && audioContextRef.current.state === 'running') {
                            audioContextRef.current.suspend();
                        }
                    }
                });
            },
            { threshold: 0.5 } // Trigger when 50% of section is visible
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        // Add click listener to enable audio on first interaction
        const handleFirstInteraction = async () => {
            if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
                try {
                    await audioContextRef.current.resume();
                    setAudioMuted(false);
                    console.log('Audio enabled on first interaction');
                    document.removeEventListener('click', handleFirstInteraction);
                    document.removeEventListener('touchstart', handleFirstInteraction);
                    document.removeEventListener('keydown', handleFirstInteraction);
                } catch (error) {
                    console.log('Failed to resume audio context:', error);
                }
            }
        };

        document.addEventListener('click', handleFirstInteraction);
        document.addEventListener('touchstart', handleFirstInteraction);
        document.addEventListener('keydown', handleFirstInteraction);

        // Cleanup function
        return () => {
            observer.disconnect();
            document.removeEventListener('click', handleFirstInteraction);
            document.removeEventListener('touchstart', handleFirstInteraction);
            document.removeEventListener('keydown', handleFirstInteraction);
            
            // Cleanup Web Audio API
            if (sourceRef.current) {
                sourceRef.current.stop();
            }
            if (audioContextRef.current) {
                audioContextRef.current.close();
            }
        };
    }, [audioEnabled, audioInitialized]);

    const enableAudio = () => {
        console.log('enableAudio called, audioRef.current:', audioRef.current);
        if (audioRef.current) {
            console.log('Attempting to play audio...');
            audioRef.current.play().then(() => {
                setAudioEnabled(true);
                console.log('Audio enabled successfully');
            }).catch(error => {
                console.log('Audio play failed:', error);
            });
        } else {
            console.log('Audio ref is null');
        }
    };

    return (
    <section ref={sectionRef} id="home" className="parallax-section hero-section">
        {/* Background Video - Hidden on mobile */}
        <video ref={videoRef} className="hero-bg-video" autoPlay muted loop playsInline preload="auto">
            <source src="/videos/Bgvideo.MP4" type="video/mp4"/>
            <source src="/videos/Bgvideo.mp4" type="video/mp4"/>
            <source src="/videos/Bgvideo.MOV" type="video/mp4"/>
            <source src="/videos/Bgvideo.mov" type="video/mp4"/>
            Your browser does not support the video tag.
        </video>
        
        {/* Mobile Background Image - Only visible on mobile devices */}
        <div className="hero-mobile-bg d-block d-lg-none" style={{
            backgroundImage: 'url(/ImagesHotelGallery/43.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
        }}>
        </div>
        
        {/* Background Music */}
        <audio ref={audioRef} className="hero-bg-music" autoPlay muted loop preload="auto">
            <source src="/Audio/lofi-instrumental.mp3" type="audio/mpeg"/>
            Your browser does not support the audio element.
        </audio>
        
        <div id="heroCarousel" className="carousel slide hero-carousel" data-bs-ride="carousel" data-bs-interval="5000" style={{height: '100vh', position: 'relative', zIndex: 1}}>
            <div className="carousel-inner" style={{height: '100%'}}>
                {/* Empty carousel - video is the background */}
            </div>
        </div>
        
        <div className="parallax-overlay"></div>
        {audioEnabled && (
            <div style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                zIndex: 10,
                background: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '8px 12px',
                borderRadius: '20px',
                fontSize: '14px',
                backdropFilter: 'blur(10px)'
            }}>
                🎵 Music Playing
            </div>
        )}
        
        {audioEnabled && audioMuted && (
            <div style={{
                position: 'absolute',
                top: '20px',
                left: '20px',
                zIndex: 10,
                background: 'rgba(255, 255, 255, 0.9)',
                color: 'black',
                padding: '8px 12px',
                borderRadius: '20px',
                fontSize: '14px',
                backdropFilter: 'blur(10px)',
                animation: 'pulse 2s infinite'
            }}>
                🔇 Click to enable music
            </div>
        )}
        <div className="parallax-content">
            <div className="container text-center">
                                 <h1 className="display-3 fw-bold mb-4 hero-title">
                    <span>W</span><span>e</span><span>l</span><span>c</span><span>o</span><span>m</span><span>e</span><span>&nbsp;</span><span>t</span><span>o</span><span>&nbsp;</span><span>H</span><span>o</span><span>t</span><span>e</span><span>l</span><span>&nbsp;</span><span>H</span><span>i</span><span>l</span><span>l</span><span>t</span><span>o</span><span>p</span>
                </h1>
                 <p className="lead mb-4 hero-subtitle">Where Ancient Forts Meet Pristine Nature</p>
                 <div className="row justify-content-center">
                     <div className="col-md-8">
                         <p className="mb-4 hero-description">Experience luxury amidst the echoes of history and the whispers of untouched wilderness. Our resort offers the perfect blend of heritage and tranquility.</p>
                     </div>
                 </div>
                 <div className="hero-buttons">
                     <a href="#contact" className="btn btn-primary btn-lg">Book Your Stay</a>
                     <a href="#about" className="btn btn-outline-light btn-lg">Discover More</a>
                 </div>
            </div>
        </div>
    </section>

    );
};
export default Slider;